import React, { useCallback, useEffect, useState } from 'react';
import { useI18n } from './i18n';
import AttachmentPicker from './AttachmentPicker';
import { isAuthenticated } from './auth';
import { listAttachments, uploadAttachments, deleteAttachment, fetchAttachmentBlobUrl, describeUploadFailure, getCurrentUserId, isCatalogOwner, MAX_LABELS } from './attachments';

// Gallery of the files attached to a sheet. Anyone who can see the sheet sees the files; the owner (canEdit)
// can also add and delete them. Props: sheetId (uuid, legacy) or ownerType 'sheets' | 'wines' | 'beers' | 'products' + ownerId,
// canEdit (bool, sheets only), onChange() optional.
// Wine, beer and product galleries hold label photos: public to read; any signed-in user can add (max 4), and only the user
// who uploaded a photo can delete it.
function AttachmentList({ sheetId, ownerType = 'sheets', ownerId, canEdit = false, onChange }) {
  const { t } = useI18n();
  const id = ownerId || sheetId;
  const catalog = isCatalogOwner(ownerType);
  const [userId, setUserId] = useState(null);
  const [items, setItems] = useState([]);
  const [thumbs, setThumbs] = useState({});
  const [pending, setPending] = useState([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);

  const load = useCallback(async () => {
    try {
      const list = await listAttachments(id, ownerType);
      setItems(list);
      const urls = {};
      await Promise.all(list.filter((a) => a.thumb_available).map(async (a) => {
        urls[a.id] = await fetchAttachmentBlobUrl(a.id, 'thumb');
      }));
      setThumbs((prev) => { Object.values(prev).forEach((u) => u && URL.revokeObjectURL(u)); return urls; });
    } catch { /* ignore */ }
  }, [id, ownerType]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (catalog && isAuthenticated()) getCurrentUserId().then(setUserId); }, [catalog]);
  useEffect(() => () => { Object.values(thumbs).forEach((u) => u && URL.revokeObjectURL(u)); }, []); // eslint-disable-line

  const openFile = async (attachment) => {
    const url = await fetchAttachmentBlobUrl(attachment.id, 'file');
    if (url) window.open(url, '_blank', 'noopener');
  };

  const upload = async () => {
    setBusy(true);
    const { uploaded, failed } = await uploadAttachments(id, pending, ownerType);
    setBusy(false);
    setPending(failed.length ? pending.filter((p) => failed.some((f) => f.name === p.file.name)) : []);
    setMessage(failed.length ? { type: 'error', text: failed.map((f) => `${f.name}: ${describeUploadFailure(f, t)}`).join(' · ') } : { type: 'success', text: t('attachments.uploaded', { n: uploaded }) });
    await load();
    if (onChange) onChange();
  };

  const remove = async (attachment) => {
    if (!window.confirm(t('attachments.confirm_delete'))) return;
    if (await deleteAttachment(attachment.id)) { await load(); if (onChange) onChange(); }
    else setMessage({ type: 'error', text: t('attachments.error.delete_failed') });
  };

  const mayUpload = catalog ? isAuthenticated() && items.length < MAX_LABELS : canEdit;
  const mayDelete = (a) => (catalog ? !!userId && a.uploader_id === userId : canEdit);
  if (items.length === 0 && !mayUpload) return null;

  return (
    <div className="attachment-list">
      <h3>{t(catalog ? 'attachments.labels_title' : 'attachments.title')} ({items.length})</h3>
      {items.length === 0 && <p className="empty">{t(catalog ? 'attachments.none_labels' : 'attachments.none')}</p>}
      <div className="attachment-grid">
        {items.map((a) => (
          <figure key={a.id} className="attachment-card">
            <button type="button" className="attachment-thumb" onClick={() => openFile(a)} title={a.original_name}>
              {thumbs[a.id] ? <img src={thumbs[a.id]} alt={a.caption || a.original_name} /> : <span className="attachment-pdf">PDF</span>}
            </button>
            <figcaption>
              {!catalog && <small>{t(`attachments.role.${a.role}`)}</small>}
              {a.caption && <div>{a.caption}</div>}
              {mayDelete(a) && <button type="button" className="chip" onClick={() => remove(a)}>{t('attachments.delete')}</button>}
            </figcaption>
          </figure>
        ))}
      </div>
      {mayUpload && (
        <div style={{ marginTop: 10 }}>
          <AttachmentPicker items={pending} onChange={setPending} existingCount={items.length} ownerType={ownerType} />
          {pending.length > 0 && (
            <button type="button" className="submit-btn" disabled={busy} onClick={upload} style={{ marginTop: 8 }}>
              {busy ? t('attachments.uploading') : t('attachments.upload')}
            </button>
          )}
        </div>
      )}
      {message && <div className={`message ${message.type}`} style={{ marginTop: 8 }}>{message.text}</div>}
    </div>
  );
}

export default AttachmentList;
